/**
 * Gift-city onboarding status for the PPFAS gift fund.
 *
 * Belongs here: status enum, response type, GET, and the React Query hook.
 * Does not belong here: which sheet to open.
 */
import { useQuery } from '@tanstack/react-query'
import { mobileBackendUrl } from '../../env'
import axiosInstance from '../../utils/axiosConfig'

export enum GiftCityOnboardingStatus {
  NOT_APPLICABLE = 'NOT_APPLICABLE',
  NOT_INITIATED = 'NOT_INITIATED',
  SUCCESS = 'SUCCESS'
}

export type GiftCityOnboardingStatusResponse = {
  onboardingStatus?: string | null
}

/** GET gift-city onboarding status. Throws when the status is missing or unknown. */
export async function fetchGiftCityOnboardingStatus(isin: string): Promise<GiftCityOnboardingStatus> {
  const response = await axiosInstance.get<GiftCityOnboardingStatusResponse>(
    `${mobileBackendUrl}/v1/mutual-fund/gift-city-onboarding-status/${isin}`
  )
  const onboardingStatus = response?.data?.onboardingStatus
  if (
    onboardingStatus === GiftCityOnboardingStatus.NOT_APPLICABLE ||
    onboardingStatus === GiftCityOnboardingStatus.NOT_INITIATED ||
    onboardingStatus === GiftCityOnboardingStatus.SUCCESS
  ) {
    return onboardingStatus
  }
  throw new Error('Gift city onboarding status missing')
}

/** Onboarding status for one ISIN. Idle until `refetch`, unless `enabled` fetches on mount. */
export function useGiftCityOnboardingStatusQuery(isin: string, enabled = false) {
  return useQuery({
    queryKey: ['gift-city-onboarding-status', isin],
    queryFn: () => fetchGiftCityOnboardingStatus(isin),
    enabled
  })
}
